//import { BridgeProxy } from 'pos/common';
import { Res, SaleDTO, TicketDTO, TicketLineDTO } from 'core/api';
import { BridgeProxy } from 'pos/common';
import { Display } from './Display';
import * as UI from 'core/ui';

const LINE_LENGTH = 20;

async function show(line1: string, line2: string) {
	await BridgeProxy.invoke('NO_REGISTER_USER_ACTIVITY', BridgeProxy.Endpoints.CustomerDisplay.Display, { line1, line2 });
}

function padToLength(value: string, length: number): string {
	return value.padEnd(length, ' ').substring(0, length);
}

function alignMiddle(str: string, length: number) {
	if (str.length > length)
		str = str.substring(0, length);

	const leftSpaces = (length - str.length) / 2;
	const rightSpaces = length - str.length - leftSpaces;
	return padToLength('', leftSpaces) + str + padToLength('', rightSpaces);
}

async function displayTicketLine(accumulatedAmount: number, qty: number, productPrice: number, productName: string, appliedDiscount = '') {
	const quantity = UI.Format.asShortQuantity(qty);
	const price = UI.Format.asPrice(productPrice);
	const product = padToLength(productName, Math.max(LINE_LENGTH - (quantity.length + ' x '.length + ' '.length + price.length)));
	const line1 = Res.Const.ADDED_LINE_DISPLAY_LBL(quantity, product, price);

	const total = Res.Const.TOTAL_DISPLAY_LBL(UI.Format.asPrice(accumulatedAmount));

	const space = padToLength('', Math.max(LINE_LENGTH - total.length - appliedDiscount.length, 0));
	const line2 = appliedDiscount.length > 0 ? appliedDiscount + space + total : padToLength('', Math.max(LINE_LENGTH - total.length, 0)) + total;

	await show(padToLength(line1, LINE_LENGTH), padToLength(line2, LINE_LENGTH));
}

const display: Display = {


	async clear(): Promise<void> {
		await show('', '');
	},

	async displayTotal(amount: number): Promise<void> {
		const total = Res.Const.TOTAL_DISPLAY_LBL(UI.Format.asPrice(amount));
		const line2 = padToLength('', Math.max(LINE_LENGTH - total.length, 0)) + total;
		await show('', padToLength(line2, LINE_LENGTH));
	},

	async displayTicketTotal(ticket: TicketDTO): Promise<void> {
		await this.displayTotal(ticket.PendingAmount);
	},

	async displayOffers(ticket: TicketDTO): Promise<void> {
		await this.displayTotal(ticket.PendingAmount);
	},

	async displaySale(sale: SaleDTO): Promise<void> {
		const line1 = Res.Const.CHANGE_DISPLAY_LBL(UI.Format.asPrice(sale.PaidAmount), UI.Format.asPrice(sale.ChangeAmount));
		const total = Res.Const.TOTAL_DISPLAY_LBL(UI.Format.asPrice(sale.NetAmount));
		const line2 = padToLength('', Math.max(LINE_LENGTH - total.length, 0)) + total;
		await show(padToLength(line1, LINE_LENGTH), padToLength(line2, LINE_LENGTH));
	},

	async displayNewLine(ticket: TicketDTO, line: TicketLineDTO): Promise<void> {
		if (line.AppliedOfferCode === '' || line.AppliedOfferCode === null)
			await displayTicketLine(ticket.PendingAmount, line.Quantity, line.UnitPrice, line.Product.ProductName);
		// Si la línea es nueva y tiene una promoción, pinto el descuento ligado a la promoción
		else if (line.HasCashDiscount)
			await this.displayLineCashDiscount(ticket, line);
		else if (line.HasDiscountPercentage)
			await this.displayLineDiscountPercentage(ticket, line);
	},

	async displayExistingLine(ticket: TicketDTO, line: TicketLineDTO): Promise<void> {
		await displayTicketLine(ticket.PendingAmount, line.Quantity, line.UnitPrice, line.Product.ProductName);
	},

	async displayRemovedLine(ticket: TicketDTO, line: TicketLineDTO): Promise<void> {
		await displayTicketLine(ticket.PendingAmount, -line.Quantity, line.UnitPrice, line.Product.ProductName);
	},

	async displayLineDiscountPercentage(ticket: TicketDTO, line: TicketLineDTO): Promise<void> {
		const discount = line.DiscountPercentage !== 0 ? `-${UI.Format.asDecimalWith(2)(line.DiscountPercentage)}%` : '';
		await displayTicketLine(ticket.PendingAmount, line.Quantity, line.UnitPrice, line.Product.ProductName, discount);
	},

	async displayLineCashDiscount(ticket: TicketDTO, line: TicketLineDTO): Promise<void> {
		const discount = line.CashDiscount !== 0 ? UI.Format.asPrice(-line.CashDiscount) : '';
		await displayTicketLine(ticket.PendingAmount, line.Quantity, line.UnitPrice, line.Product.ProductName, discount);
	},

	async displayDiscounts(ticket: TicketDTO): Promise<void> {
		const pendingAmount = ticket.PendingAmount;
		const discountPercentage = ticket.DiscountPercentage;
		const cashDiscount = ticket.CashDiscount;

		const percentage = discountPercentage !== 0 ? `${UI.Format.asDecimalWith(2)(discountPercentage)}%` : '';
		const cash = cashDiscount !== 0 ? UI.Format.asPrice(cashDiscount) : '';
		const dtos = percentage + '  ' + cash;
		const line1 = cashDiscount !== 0 || discountPercentage !== 0 ? Res.Const.DISCOUNT_DISPLAY_LBL(dtos) : '';

		const total = Res.Const.TOTAL_DISPLAY_LBL(UI.Format.asPrice(pendingAmount));
		const line2 = padToLength('', Math.max(LINE_LENGTH - total.length, 0)) + total;

		await show(padToLength(line1, LINE_LENGTH), padToLength(line2, LINE_LENGTH));

	},

	async displayClosedCashdrawer(): Promise<void> {
		const line1 = alignMiddle(Res.Const.CLOSED_CASHDRAWER_DISPLAY_LBL(), LINE_LENGTH);
		await show(line1, '');
	},

	async displayLineNote(ticket: TicketDTO, line: TicketLineDTO): Promise<void> {
		await displayTicketLine(ticket.PendingAmount, line.Quantity, line.UnitPrice, line.Product.ProductName);
	},

	async displayWeight(kgs: number, pricePerKgs: number, total: number): Promise<void> {
		await show(
			Res.Const.PRODUCT_WEIGHT_DISPLAY_LBL(),
			`${UI.Format.asQuantity(kgs).padEnd(6, ' ')} ${UI.Format.asPrice(pricePerKgs).padEnd(6, ' ')} ${UI.Format.asPrice(total).padStart(6, ' ')}`);
	},

	async displayCustomer(ticket: TicketDTO): Promise<void> {
		await this.displayTotal(ticket.PendingAmount);
	},

	async displayPriceList(ticket: TicketDTO): Promise<void> {
		await this.displayTotal(ticket.PendingAmount);
	},

	async displayServiceCharge(ticket: TicketDTO): Promise<void> {

		await show(
			padToLength(Res.Const.SERVICE_CHARGE_DISPLAY_LBL(ticket.AppliedServiceCharge.ChargePercentage), LINE_LENGTH),
			padToLength(UI.Format.asPrice(ticket.ServiceChargeAmount), LINE_LENGTH)
		);
	},
};

export default display;